export type SupportedLanguage = 'en' | 'nl' | 'de'

export type UserPreferenceChanges = {
  timeZoneId?: string | null
  preferredLanguage?: SupportedLanguage | null
  formattingLocale?: string | null
}

export const supportedLanguages: SupportedLanguage[] = ['en', 'nl', 'de']
export const supportedFormattingLocales = ['en-GB', 'nl-NL', 'de-DE'] as const

export const detectBrowserLanguage = (): SupportedLanguage => {
  if (typeof navigator === 'undefined') return 'en'
  const language = navigator.language.split('-')[0].toLowerCase()
  return supportedLanguages.includes(language as SupportedLanguage)
    ? (language as SupportedLanguage)
    : 'en'
}

export const useUserPreferences = () => {
  const authStore = useAuthStore()
  const { locale, setLocale } = useI18n()

  const savePreferences = async (changes: UserPreferenceChanges) => {
    const has = (key: keyof UserPreferenceChanges) => Object.hasOwn(changes, key)
    const timeZoneId = has('timeZoneId') ? changes.timeZoneId : authStore.timeZoneId
    const preferredLanguage = has('preferredLanguage')
      ? changes.preferredLanguage
      : authStore.preferredLanguage
    const formattingLocale = has('formattingLocale')
      ? changes.formattingLocale
      : authStore.formattingLocale

    await useApiClient('/users/current/preferences', {
      method: 'PATCH',
      body: {
        timeZoneId: timeZoneId || null,
        preferredLanguage: preferredLanguage || null,
        formattingLocale: formattingLocale || null,
      },
    })

    authStore.timeZoneId = timeZoneId || undefined
    authStore.preferredLanguage = preferredLanguage || undefined
    authStore.formattingLocale = formattingLocale || undefined
    setTimeZonePreference(authStore.timeZoneId)
    setFormattingLocalePreference(authStore.formattingLocale)

    const nextLanguage = authStore.preferredLanguage || detectBrowserLanguage()
    if (locale.value !== nextLanguage) await setLocale(nextLanguage)
  }

  return { savePreferences }
}
